import React from 'react';
import { Clock, TrendingUp } from 'lucide-react';
import { Program } from '../../../types/program';

interface ProgramCardProps {
  program: Program;
  onClick: (programId: string) => void;
}

const ProgramCard: React.FC<ProgramCardProps> = ({ program, onClick }) => {
  // Use _id if id is not available (MongoDB format)
  const programId = program.id || (program as any)._id;
  
  // Build full image URL from relative path
  const getImageUrl = (imageUrl: string | undefined) => {
    if (!imageUrl) return null;
    
    // If already a full URL, return as-is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // Build full URL from relative path
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8080';
    return `${baseUrl}${imageUrl}`;
  };

  const fullImageUrl = getImageUrl(program.imageUrl);

  return (
    <div
      onClick={() => onClick(programId)}
      className="bg-[#141414] rounded-2xl border border-[#1F1F1F] overflow-hidden hover:border-[#23F47C] hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(35,244,124,0.15)] transition-all duration-300 cursor-pointer group"
    >
      {/* Cover Image - Height: 240px */}
      <div className="relative h-60 bg-[#1F1F1F] overflow-hidden">
        {fullImageUrl ? (
          <img
            src={fullImageUrl}
            alt={program.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              // Fallback to placeholder if image fails to load
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <TrendingUp size={48} className="text-[#5F5F5F]" />
          </div>
        )}
        
        {/* Difficulty Badge */}
        <div className="absolute top-4 right-4">
          <span className="px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wide bg-[rgba(10,10,10,0.8)] backdrop-blur-lg text-[#23F47C]">
            {program.difficulty}
          </span>
        </div>
      </div>

      {/* Content Section - Padding: 20px 24px 24px 24px */}
      <div className="px-6 pt-5 pb-6">
        <h3 className="text-white text-xl font-bold leading-7 mb-2 truncate">
          {program.title}
        </h3>
        
        <p className="text-[#8F8F8F] text-sm leading-5 mb-4 line-clamp-2">
          {program.description}
        </p>

        {/* Metadata Row - Border-top separator, 16px gap */}
        <div className="flex items-center gap-4 pt-4 mt-4 border-t border-[#1F1F1F]">
          <div className="flex items-center gap-1.5 text-[#8F8F8F] text-[13px] font-medium">
            <Clock size={16} className="text-[#23F47C]" />
            <span>{program.durationWeeks} weeks</span>
          </div>
          
          {program.tags && program.tags.length > 0 && (
            <div className="flex items-center gap-1.5 ml-auto">
              {program.tags.slice(0, 1).map((tag, index) => (
                <span key={index} className="px-2 py-1 bg-[rgba(35,244,124,0.1)] text-[#23F47C] rounded text-xs font-medium">
                  {tag}
                </span>
              ))}
              {program.tags.length > 1 && (
                <span className="text-[#8F8F8F] text-xs">
                  +{program.tags.length - 1}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgramCard;
